import { request } from "../http/api-client";
import type { ServerTransactionsResponse, UITxn, TransactionsResponse, TransactionFilters } from "../types";

const statusMap: Record<string, UITxn["status"]> = {
  SUCCESS: "success",
  PENDING: "pending",
  FAILED: "failed",
  PROCESSING: "processing",
  CANCELLED: "cancelled",
  CANCELED: "cancelled", // Handle both spellings
  EXPIRED: "expired",
};

export async function fetchTransactions(filters: TransactionFilters = {}): Promise<TransactionsResponse> {
  const {
    page = 1,
    limit = 10,
    status,
    type,
    dateFrom,
    dateTo,
    minAmount,
    maxAmount,
    search
  } = filters;

  const params = new URLSearchParams();
  params.set("page", String(page));
  params.set("limit", String(limit));

  // Handle status - can be multiple values separated by comma
  if (status && status !== "all-status") {
    // Convert UI status values to backend format
    const backendStatus = status.toUpperCase();
    if (backendStatus.includes(",")) {
      // Handle multiple statuses
      const statusArray = backendStatus.split(",");
      params.set("status", statusArray.join(","));
    } else {
      params.set("status", backendStatus);
    }
  }

  // Handle type - can be PAYIN, PAYOUT, or both
  if (type && type !== "all") {
    if (type === "payin") {
      params.set("type", "PAYIN");
    } else if (type === "payout") {
      params.set("type", "PAYOUT");
    } else {
      params.set("type", type.toUpperCase());
    }
  }

  // Handle both types if not specified
  if (!type || type === "all") {
    params.set("type", "PAYIN,PAYOUT");
  }

  // Date range
  if (dateFrom) params.set("dateFrom", dateFrom);
  if (dateTo) params.set("dateTo", dateTo);

  // Amount range
  if (minAmount) params.set("minAmount", String(minAmount));
  if (maxAmount) params.set("maxAmount", String(maxAmount));

  // Search (reference ID or customer info)
  if (search) {
    params.set("search", search);
  }

  const res = await request<ServerTransactionsResponse>(`/api/v1/transactions?${params.toString()}`, {
    method: "GET"
  });

  const { transactions, summary, pagination } = res.data;

  // Transform backend data to UI format
  const uiTransactions: UITxn[] = transactions.map((t) => {
    const type = t.type === "PAYOUT" ? "payout" : "payin";
    const status = statusMap[(t.status || "").toUpperCase()] || "pending";

    // Extract customer info
    const customerName = t.customer?.name || t.customer?.email || "-";
    const customer = customerName;

    // Extract method from transaction object or customer object
    const method = t.paymentMethod || t.customer?.method || t.customer?.paymentMethod || "N/A";

    // Handle dates
    const created = t.createdAt;
    const completed = t.completedAt || created;
    const d = new Date(completed);
    const date = d.toISOString().slice(0, 10);
    const time = d.toISOString().slice(11, 16);

    // Reference ID
    const referenceId = t.reference || t.id.substring(0, 8) + '...';

    return {
      id: t.id,
      referenceId,
      type,
      status,
      customer,
      method,
      amount: t.amount,
      fee: t.feeAmount,
      net: t.netAmount,
      currency: t.currency,
      date,
      time,
      errorMessage: null,
      raw: t,
    } as UITxn;
  });

  return {
    transactions: uiTransactions,
    summary,
    pagination,
  };
}

export async function getTransactionStats(period: "today" | "week" | "month" | "last90d" = "today") {
  const res = await request<{ success: boolean; data: any }>(`/api/v1/transactions/stats?period=${period}`);
  const data = res.data || {};
  const keyMap: Record<string, string> = {
    today: "today",
    week: "thisWeek",
    month: "thisMonth",
    last90d: "last90Days",
  };
  const k = keyMap[period];
  const s = data[k] || {};
  return {
    totalCount: s.totalCount || 0,
    totalAmount: s.totalAmount || 0,
    totalFees: s.totalFee || 0,
    totalNet: s.totalNet || 0,
    successRate: s.successRate || 0,
    successAmount: s.totalPayinSuccess || 0, // Today's Payin
    successCount: s.successCount || 0,
    payinCount: s.payinCount || 0,
    payinAmount: s.totalPayinSuccess || 0, // Today's Payin
    payoutCount: s.payoutCount || 0,
    payoutAmount: s.totalPayoutSuccess || 0, // Today's Payout
  };
}